import { useLayoutEffect, useState } from 'react'

/** Sets `node`'s font size so its text fits its width on one line. */
export function fitOneLine(node: HTMLElement, minPx: number) {
  node.style.fontSize = ''
  node.style.whiteSpace = 'nowrap'
  const max = parseFloat(getComputedStyle(node).fontSize)
  const available = node.clientWidth
  if (!max || !available || node.scrollWidth <= available) return
  let size = Math.max(minPx, Math.floor((max * available) / node.scrollWidth * 2) / 2)
  node.style.fontSize = `${size}px`
  // Sub-pixel rounding can leave it a hair too wide; step down until it fits.
  while (size > minPx && node.scrollWidth > node.clientWidth) {
    size -= 0.5
    node.style.fontSize = `${size}px`
  }
  if (node.scrollWidth > node.clientWidth) node.style.whiteSpace = ''
}

/**
 * Keeps a heading on one line by shrinking its font just enough to fit its
 * width (owner, 2026-10-01: the login title wrapped onto two lines). It never
 * grows past the stylesheet's size, and below `minPx` it gives up and lets the
 * text wrap rather than become unreadable. It refits when the width changes
 * (panel margin and padding, screen size) and once the web fonts have loaded,
 * since a custom LINE font changes the text's width.
 *
 * Returns a callback ref, so it also fits an element that mounts later (the
 * Settings preview exists only while its tab is open).
 *
 * A twin of liff/src/hooks/useFitOneLine.ts (the LINE login page): keep the
 * two in step.
 */
export function useFitOneLine(text: string, minPx = 14) {
  const [el, setEl] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    if (!el) return

    const fit = () => fitOneLine(el, minPx)

    fit()
    // Watch the parent: its width is what the title has to fit, and it does
    // not change when the title's own font size does.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit)
    if (el.parentElement) observer?.observe(el.parentElement)
    let active = true
    document.fonts?.ready.then(() => { if (active) fit() })
    return () => {
      active = false
      observer?.disconnect()
    }
  }, [el, text, minPx])

  return setEl
}
